import { apiClient } from './api';
import type {
  JobPosting, Candidate, Interview, OfferLetter, HiringStats,
  CreateJobData, CreateCandidateData, ScheduleInterviewData, CreateOfferData,
  CandidateStage, HiringPipelineStats, CandidateEmail, TalentPool, JobCollaborator,
} from '../types/hiring.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export async function getJobPostings(status?: string, branchId?: string): Promise<JobPosting[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (branchId) params.set('branchId', branchId);
  const qs = params.toString();
  const res = await apiClient.get<ApiResponse<JobPosting[]>>(`/api/hiring/jobs${qs ? `?${qs}` : ''}`);
  return res.data.data;
}

export async function getJobById(id: string): Promise<JobPosting & { pipeline_stats: HiringPipelineStats }> {
  const res = await apiClient.get<ApiResponse<JobPosting & { pipeline_stats: HiringPipelineStats }>>(`/api/hiring/jobs/${id}`);
  return res.data.data;
}

export async function createJob(data: CreateJobData): Promise<JobPosting> {
  const res = await apiClient.post<ApiResponse<JobPosting>>('/api/hiring/jobs', data);
  return res.data.data;
}

export async function updateJob(id: string, data: Partial<CreateJobData>): Promise<JobPosting> {
  const res = await apiClient.patch<ApiResponse<JobPosting>>(`/api/hiring/jobs/${id}`, data);
  return res.data.data;
}

export async function publishJob(id: string): Promise<JobPosting> {
  const res = await apiClient.patch<ApiResponse<JobPosting>>(`/api/hiring/jobs/${id}/publish`, {});
  return res.data.data;
}

export async function closeJob(id: string): Promise<JobPosting> {
  const res = await apiClient.patch<ApiResponse<JobPosting>>(`/api/hiring/jobs/${id}/close`, {});
  return res.data.data;
}

export async function discardJob(id: string): Promise<void> {
  await apiClient.delete(`/api/hiring/jobs/${id}`);
}

export async function getCandidates(jobId: string, stage?: string): Promise<Candidate[]> {
  const qs = stage ? `?stage=${stage}` : '';
  const res = await apiClient.get<ApiResponse<Candidate[]>>(`/api/hiring/jobs/${jobId}/candidates${qs}`);
  return res.data.data;
}

export async function getCandidateById(id: string): Promise<Candidate & { interviews: Interview[] }> {
  const res = await apiClient.get<ApiResponse<Candidate & { interviews: Interview[] }>>(`/api/hiring/candidates/${id}`);
  return res.data.data;
}

export async function addCandidate(jobId: string, data: CreateCandidateData): Promise<Candidate> {
  const res = await apiClient.post<ApiResponse<Candidate>>(`/api/hiring/jobs/${jobId}/candidates`, data);
  return res.data.data;
}

export async function updateCandidateStage(id: string, stage: CandidateStage): Promise<Candidate> {
  const res = await apiClient.patch<ApiResponse<Candidate>>(`/api/hiring/candidates/${id}/stage`, { stage });
  return res.data.data;
}

export async function updateCandidateRating(id: string, rating: number): Promise<Candidate> {
  const res = await apiClient.patch<ApiResponse<Candidate>>(`/api/hiring/candidates/${id}/rating`, { rating });
  return res.data.data;
}

export async function addCandidateNote(id: string, note: string): Promise<Candidate> {
  const res = await apiClient.post<ApiResponse<Candidate>>(`/api/hiring/candidates/${id}/notes`, { note });
  return res.data.data;
}

export async function rejectCandidate(id: string, reason: string): Promise<Candidate> {
  const res = await apiClient.patch<ApiResponse<Candidate>>(`/api/hiring/candidates/${id}/reject`, { reason });
  return res.data.data;
}

export async function scheduleInterview(candidateId: string, data: ScheduleInterviewData): Promise<Interview> {
  const res = await apiClient.post<ApiResponse<Interview>>(`/api/hiring/candidates/${candidateId}/interviews`, data);
  return res.data.data;
}

export async function updateInterview(id: string, data: Partial<ScheduleInterviewData>): Promise<Interview> {
  const res = await apiClient.patch<ApiResponse<Interview>>(`/api/hiring/interviews/${id}`, data);
  return res.data.data;
}

export async function submitInterviewFeedback(
  id: string,
  feedback: string,
  rating: number,
  recommendation: string,
): Promise<Interview> {
  const res = await apiClient.patch<ApiResponse<Interview>>(`/api/hiring/interviews/${id}/feedback`, {
    feedback, rating, recommendation,
  });
  return res.data.data;
}

export async function createOfferLetter(candidateId: string, data: CreateOfferData): Promise<OfferLetter> {
  const res = await apiClient.post<ApiResponse<OfferLetter>>(`/api/hiring/candidates/${candidateId}/offer`, data);
  return res.data.data;
}

export async function sendOfferLetter(offerId: string): Promise<OfferLetter> {
  const res = await apiClient.patch<ApiResponse<OfferLetter>>(`/api/hiring/offers/${offerId}/send`, {});
  return res.data.data;
}

export async function uploadCV(candidateId: string, file: File): Promise<Candidate> {
  const formData = new FormData();
  formData.append('cv', file);
  const res = await apiClient.post<ApiResponse<Candidate>>(`/api/hiring/candidates/${candidateId}/cv`, formData);
  return res.data.data;
}

/** A one-hour signed link to the candidate's CV. The CV bucket is private, so there is no permanent URL. */
export async function getCandidateCvUrl(candidateId: string): Promise<string> {
  const res = await apiClient.get<ApiResponse<{ url: string }>>(`/api/hiring/candidates/${candidateId}/cv`);
  return res.data.data.url;
}

export interface ParsedCvFields {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  candidate_role?: string;
  current_company?: string;
  current_location?: string;
  linkedin_url?: string;
}

export async function parseCvFile(file: File): Promise<ParsedCvFields | null> {
  const formData = new FormData();
  formData.append('cv', file);
  const res = await apiClient.post<{ success: boolean; mapped?: ParsedCvFields; cv_parsed: boolean }>(
    '/api/hiring/cv/parse',
    formData,
  );
  return res.data.cv_parsed && res.data.mapped ? res.data.mapped : null;
}

export async function getHiringStats(): Promise<HiringStats> {
  const res = await apiClient.get<ApiResponse<HiringStats>>('/api/hiring/stats');
  return res.data.data;
}

export async function recordJobView(id: string): Promise<void> {
  try {
    await apiClient.patch(`/api/hiring/jobs/${id}/view`, {});
  } catch {
    // non-blocking
  }
}

export async function getCollaborators(jobId: string): Promise<JobCollaborator[]> {
  const res = await apiClient.get<ApiResponse<JobCollaborator[]>>(`/api/hiring/jobs/${jobId}/collaborators`);
  return res.data.data;
}

export async function addCollaborator(jobId: string, userId: string, role: string): Promise<JobCollaborator> {
  const res = await apiClient.post<ApiResponse<JobCollaborator>>(`/api/hiring/jobs/${jobId}/collaborators`, { user_id: userId, role });
  return res.data.data;
}

export async function removeCollaborator(jobId: string, collaboratorId: string): Promise<void> {
  await apiClient.delete(`/api/hiring/jobs/${jobId}/collaborators/${collaboratorId}`);
}

export async function updateCollaboratorNotifications(jobId: string, collaboratorId: string, emailNotifications: boolean): Promise<void> {
  await apiClient.patch(`/api/hiring/jobs/${jobId}/collaborators/${collaboratorId}`, { email_notifications: emailNotifications });
}

export async function getTalentPools(): Promise<TalentPool[]> {
  const res = await apiClient.get<ApiResponse<TalentPool[]>>('/api/hiring/talent-pools');
  return res.data.data;
}

export async function createTalentPool(name: string, description?: string): Promise<TalentPool> {
  const res = await apiClient.post<ApiResponse<TalentPool>>('/api/hiring/talent-pools', { name, description });
  return res.data.data;
}

export async function deleteTalentPool(id: string): Promise<void> {
  await apiClient.delete(`/api/hiring/talent-pools/${id}`);
}

export async function addToTalentPool(poolId: string, candidateId: string, notes?: string): Promise<void> {
  await apiClient.post(`/api/hiring/talent-pools/${poolId}/candidates`, { candidate_id: candidateId, notes });
}

export async function removeFromTalentPool(poolId: string, candidateId: string): Promise<void> {
  await apiClient.delete(`/api/hiring/talent-pools/${poolId}/candidates/${candidateId}`);
}

export async function getAllCandidates(params: { search?: string; jobId?: string; stage?: string; page?: number }): Promise<{ data: Candidate[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.jobId) qs.set('jobId', params.jobId);
  if (params.stage) qs.set('stage', params.stage);
  if (params.page) qs.set('page', String(params.page));
  const res = await apiClient.get<{ success: boolean; data: Candidate[]; total: number }>(`/api/hiring/candidates?${qs.toString()}`);
  return { data: res.data.data, total: res.data.total };
}

export async function getCandidateEmails(candidateId: string): Promise<CandidateEmail[]> {
  const res = await apiClient.get<ApiResponse<CandidateEmail[]>>(`/api/hiring/candidates/${candidateId}/emails`);
  return res.data.data;
}

export async function sendCandidateEmail(candidateId: string, subject: string, body: string): Promise<CandidateEmail> {
  const res = await apiClient.post<ApiResponse<CandidateEmail>>(`/api/hiring/candidates/${candidateId}/emails`, { subject, body });
  return res.data.data;
}
